import "./BackgroundJobsDrawer.scss";

import cx from "classnames";
import ErrorState from "components/Error/ErrorState";
import {
  formatStatusLabel,
  getStatusVariant,
  TableCellStatusPill,
} from "components/TableCellStatusPill/TableCellStatusPill";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import { formatMinutes } from "utils/date-utils";
import { useSelector } from "utils/redux";

import RetryJobButton from "./RetryJobButton";

const COLUMN_NAMES: [string, string][] = [
  ["actions", "Actions"],
  ["id", "ID"],
  ["created", "Started at"],
  ["last_status_update_at", "Last Status Update At"],
  ["duration", "Duration"],
  ["job_type", "Job Type"],
  ["status", "Status"],
  ["status_message", "Status Message"],
  ["started_by", "Started By"],
  ["file_id", "File No"],
  ["rre_name", "RRE"],
  ["bulk_id", "Bulk ID"],
];

type BackgroundJobTableRow = {
  id: number;
  created: string;
  duration: string;
  job_type: string;
  status: string;
  status_message: string;
  started_by: string;
  last_status_update_at: string | number;
  file_id: string;
  rre_name: string;
  bulk_id: string;
  can_retry: boolean;
};

export type BackgroundJobsFilters = {
  file_id?: string | string[];
  msa_id?: string | string[];
  cost_projection_id?: string | string[];
  lien_file_id?: string | string[];
  document_management_file_id?: string | string[];
  amended_review_screening_id?: string | string[];
  metadoc_id?: string | string[];
  job_type?: string;
  // Allow other keys but encourage known ones
  [key: string]: string | string[] | undefined;
};

type BackgroundJobsDrawerProps = {
  filters: BackgroundJobsFilters;
  emptyMessage?: string;
};

export const BackgroundJobsDrawer = ({
  filters,
  emptyMessage = "No background jobs",
}: BackgroundJobsDrawerProps) => {
  const [page, setPage] = useState(1);
  const showBackgroundJobs = useSelector(
    (state) => state.drawer.showBackgroundJobs,
  );
  const [hasActiveJobs, setHasActiveJobs] = useState(false);

  const {
    data: backgroundJobs = { results: [], count: 0 },
    isFetching,
    refetch,
    isError,
  } = useGetBackgroundJobsQuery(
    {
      filters: {
        ...filters,
        limit: PAGE_SIZE,
        offset: OFFSET(page),
      },
    },
    {
      skip: !showBackgroundJobs,
      pollingInterval: showBackgroundJobs && hasActiveJobs ? 3000 : 0,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (showBackgroundJobs) {
      refetch();
    }
  }, [showBackgroundJobs, refetch]);

  useEffect(() => {
    const nextHasActiveJobs =
      backgroundJobs?.results?.some(
        (job: BackgroundJobDataType) =>
          job.status === "Pending" || job.status === "In Progress",
      ) ?? false;

    setHasActiveJobs(nextHasActiveJobs);
  }, [backgroundJobs]);

  const rows: BackgroundJobTableRow[] = useMemo(
    () =>
      backgroundJobs?.results?.map((job: BackgroundJobDataType) => {
        let durationMinutes: number | null = null;

        if (job.duration !== null) {
          durationMinutes = job.duration > 0 ? job.duration : null;
        } else if (job.minutes_in_progress > 0) {
          durationMinutes = job.minutes_in_progress;
        }

        const formattedDuration = durationMinutes
          ? formatMinutes(durationMinutes)
          : "-";

        return {
          id: job.id,
          created: job.created,
          duration: formattedDuration,
          job_type: job.job_type,
          status: job.status,
          status_message: job.status_message,
          started_by: job.started_by,
          last_status_update_at: job.last_status_update_at ?? "",
          file_id: job.file_id ? String(job.file_id) : "-",
          rre_name: job.rre_name ?? "-",
          bulk_id: job.bulk_id ?? "-",
          can_retry: Boolean(job.can_retry),
        };
      }) || [],
    [backgroundJobs],
  );

  const columns = useMemo(
    () =>
      COLUMN_NAMES.map(([key, name]) => ({
        key,
        name,
        ...commonColumnProps,
        ...(key === "status" && {
          renderCell: ({ row }: { row: BackgroundJobTableRow }) => (
            <TableCellStatusPill
              label={formatStatusLabel(row.status)}
              variant={getStatusVariant(row.status)}
            />
          ),
        }),
        // the retry action is only offered for the job types the API can
        // re-dispatch (failed AI sort/summary), so most rows are blank here
        ...(key === "actions" && {
          width: "90px",
          sortable: false,
          renderCell: ({ row }: { row: BackgroundJobTableRow }) => (
            <RetryJobButton jobId={row.id} canRetry={row.can_retry} />
          ),
        }),
      })),
    [],
  );

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showBackgroundJobs,
      })}
    >
      <div className="documents-wrapper">
        <div className="swim-wrapper h-100">
          <div className="swim-headers">Background Jobs</div>
          <div className="data-values">
            {isError ? (
              <ErrorState />
            ) : (
              <Table
                className="background-job-table"
                columns={columns}
                rows={rows}
                rowKeyGetter={(row: BackgroundJobTableRow) => row.id}
                emptyTableMessage={emptyMessage}
                fullHeight
                onCellClick={() => {}}
                pagination={{
                  pageSize: PAGE_SIZE,
                  pageIndex: page,
                  rowCount: backgroundJobs.count,
                  onPageChange: (pageIndex) => setPage(pageIndex),
                  isLoading: isFetching,
                }}
              />
            )}
          </div>
        </div>
      </div>
      {/* table positioning interferes with tooltips, so we anchor it by id here */}
      <Tooltip id="retry-job-tooltip" className="custom-tooltip" place="top" />
    </div>
  );
};

export default BackgroundJobsDrawer;
